import { useEffect, useState } from "react";
import { WarningCircle, MapPinLine, Clock, ShieldWarning } from "@phosphor-icons/react";
import { Badge } from "./ui/badge";
import client from "../lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

const Stat = ({ label, value, tone = "#4A0000", icon: Icon }) => (
  <div className="rounded-[8px] border border-[#E9DDC6] bg-white p-4 flex items-center gap-3">
    <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0" style={{ background: `${tone}14`, color: tone }}><Icon size={20} weight="duotone" /></div>
    <div><p className="text-2xl font-semibold text-[#1A1715] leading-none tabular-nums">{value}</p><p className="text-xs text-[#8A8681] mt-1">{label}</p></div>
  </div>
);

export default function CheckinOverrides() {
  const [d, setD] = useState(null);

  useEffect(() => { client.get("/admin/checkin-overrides").then((r) => setD(r.data)).catch(() => setD({ offenders: [], recent: [], total_overrides: 0 })); }, []);
  if (!d) return null;

  const flagged = d.offenders.filter((o) => o.override_count >= 3);

  return (
    <div className="space-y-6" data-testid="checkin-overrides">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label="Total override check-ins" value={d.total_overrides} icon={ShieldWarning} />
        <Stat label="Crew with overrides" value={d.offenders.length} tone="#B08D57" icon={WarningCircle} />
        <Stat label="Repeat offenders (3+)" value={flagged.length} tone="#7E1119" icon={ShieldWarning} />
      </div>

      <div className="rounded-[8px] border border-[#E9DDC6] bg-white overflow-hidden">
        <div className="px-5 py-3.5 border-b border-[#F0ECE2]"><p className="text-sm font-semibold text-[#1A1715]">Offenders by frequency</p><p className="text-xs text-[#8A8681] mt-0.5">Manual off-site check-ins bypass the geofence — repeat use may warrant a strike.</p></div>
        {d.offenders.length === 0 ? (
          <p className="px-5 py-10 text-center text-sm text-[#9A968E]" data-testid="overrides-empty">No override check-ins yet — everyone's checking in on-site. 🎯</p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-[#9A968E] border-b border-[#F0ECE2]">
              <th className="px-5 py-2.5 font-medium">Crew</th><th className="px-3 py-2.5 font-medium">Role</th>
              <th className="px-3 py-2.5 font-medium text-right">Overrides</th><th className="px-3 py-2.5 font-medium text-right">Override rate</th>
              <th className="px-5 py-2.5 font-medium text-right">Last override</th>
            </tr></thead>
            <tbody>
              {d.offenders.map((o) => (
                <tr key={o.user_id} data-testid={`override-offender-${o.user_id}`} className={`border-b border-[#FAF9F6] ${o.override_count >= 3 ? "bg-[#7E1119]/[0.04]" : ""}`}>
                  <td className="px-5 py-3 font-medium text-[#2C2626]">{o.name}{o.override_count >= 3 && <Badge className="ml-2 bg-[#7E1119]/12 text-[#7E1119] border-[#7E1119]/25">Repeat</Badge>}</td>
                  <td className="px-3 py-3 capitalize text-[#6B6660]">{o.role}</td>
                  <td className="px-3 py-3 text-right font-semibold text-[#4A0000] tabular-nums">{o.override_count}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-[#6B6660]">{o.override_rate != null ? `${o.override_rate}%` : "—"}<span className="text-[#B7B2A8]"> of {o.total_checkins}</span></td>
                  <td className="px-5 py-3 text-right text-[#8A8681]">{fmt(o.last_override_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {d.recent.length > 0 && (
        <div className="rounded-[8px] border border-[#E9DDC6] bg-white overflow-hidden">
          <div className="px-5 py-3.5 border-b border-[#F0ECE2]"><p className="text-sm font-semibold text-[#1A1715]">Recent override events</p></div>
          <ul className="divide-y divide-[#FAF9F6]">
            {d.recent.map((e, i) => (
              <li key={i} data-testid={`override-event-${i}`} className="px-5 py-3 flex items-center gap-3 text-sm">
                <WarningCircle size={17} weight="fill" className="text-[#B08D57] shrink-0" />
                <span className="font-medium text-[#2C2626] truncate flex-1">{e.title}</span>
                <span className="text-[#6B6660] capitalize hidden sm:inline">{e.slot}</span>
                {e.distance_m != null && <span className="flex items-center gap-1 text-xs text-[#8A8681]"><MapPinLine size={13} />~{e.distance_m.toLocaleString()} m away</span>}
                <span className="flex items-center gap-1 text-xs text-[#8A8681]"><Clock size={13} />{fmt(e.at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
